import { db } from "@/apis/config/firebase";
import { http } from "@/apis/config/http";
import type {
  DownloadImagingProps,
  GenerateExcelProps,
  ImagingNotificationResponse,
  ImagingStudyNotification,
  NotificationApiPayload,
  SaveSharedRecordProps,
} from "@/features/imagings/schemas/imagings";
import { doc, onSnapshot, type Timestamp } from "firebase/firestore";

/** Documento `download_jobs/{job_id}` en Firestore (sincronizado con el worker). */
export interface IDownloadJobDocument {
  uid: string;
  ext: string;
  status: string;
  progress: number;
  created_at: Timestamp;
  updated_at: Timestamp;
  expireAt: Timestamp;
  url?: string;
}

/** Estado derivado para la UI durante onSnapshot. */
export interface IImagingDownloadJobStatus {
  status: string;
  progress?: number;
  message?: string;
  url?: string;
}

export type DownloadWorkerGb = "2gb" | "4gb" | "8gb" | "16gb";

/** Límites en MB (blob) → worker: ≤900 → 2gb; (900, 2GB] → 4gb; (2GB, 4GB] → 8gb; >4GB → 16gb */
const BLOB_MB_2GB = 2048;
const BLOB_MB_4GB = 4096;
const BLOB_MB_400 = 400;

/** Elige worker según tamaño blob (MB) del estudio (Healthcare API). */
export const selectDownloadWorkerByBlobSizeMb = (blobMb: number): DownloadWorkerGb => {
  if (blobMb <= BLOB_MB_400) return "2gb";
  if (blobMb <= BLOB_MB_2GB) return "4gb";
  if (blobMb <= BLOB_MB_4GB) return "8gb";
  return "16gb";
};

/** Texto de estado sin porcentaje: el UI muestra el % aparte. */
export const mapDownloadJobStatusToMessage = (status: string): string => {
  switch (status) {
    case "pending":
      return "En cola...";
    case "processing":
      return "Procesando...";
    case "completed":
      return "¡Descarga lista!";
    case "failed":
      return "Error en la descarga";
    default:
      return "Procesando...";
  }
};

/** GET `/api/v1/imaging/dicom/metrics/study/{studyUid}` — devuelve blob en MB. */
export const getDicomStudyBlobSizeMb = async (studyUid: string): Promise<number> => {
  const token = localStorage.getItem("authToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/v1/imaging/dicom/metrics/study/${encodeURIComponent(studyUid)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const text = await response.text();
    console.error("Error en getDicomStudyBlobSizeMb:", response.status, text);
    throw new Error("No se pudieron obtener las métricas del estudio");
  }

  const json = (await response.json()) as { data?: { blobStorageSizeMb?: string | number } };
  const raw = json?.data?.blobStorageSizeMb;
  const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error("Métricas del estudio no disponibles");
  }
  return n;
};

// Tiempo máximo de espera del listener (60 min)
const JOB_LISTENER_MAX_MS = 60 * 60 * 1000;

/**
 * POST `.../imaging/job/:size/download` con uid+ext.
 * Primero obtiene métricas para elegir worker (2gb–16gb).
 */
export const createImagingDownloadJob = async (params: {
  uid: string;
  ext: string;
}): Promise<{ job_id: string; status: string; url?: string }> => {
  const blobMb = await getDicomStudyBlobSizeMb(params.uid);
  const worker = selectDownloadWorkerByBlobSizeMb(blobMb);

  const token = localStorage.getItem("authToken");
  const createResponse = await fetch(
    `${import.meta.env.VITE_API_URL}/imaging/job/${worker}/download`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uid: params.uid, ext: params.ext }),
    }
  );

  if (!createResponse.ok) {
    let message = "No se pudo crear el trabajo de descarga";
    try {
      const errorData = (await createResponse.json()) as {
        error?: string;
        message?: string;
      };
      message = errorData.error ?? errorData.message ?? message;
    } catch {
      /* texto no JSON */
    }
    throw new Error(message);
  }

  return createResponse.json() as Promise<{
    job_id: string;
    status: string;
    url?: string;
  }>;
};

/**
 * Escucha `download_jobs/{job_id}` hasta completed/failed.
 * No usar para job_id artificial tipo `existing-*` (archivo ya en GCS: no hay documento).
 */
export const pollImagingDownloadJobWithProgress = async (
  jobId: string,
  onProgress?: (data: IImagingDownloadJobStatus) => void
): Promise<string> => {
  if (jobId.startsWith("existing-")) {
    throw new Error("Este trabajo no tiene seguimiento en Firestore");
  }

  return new Promise((resolve, reject) => {
    let unsubscribe: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      unsubscribe?.();
      reject(new Error("Tiempo de espera agotado para la descarga (60 min)"));
    }, JOB_LISTENER_MAX_MS);

    const finish = (fn: () => void) => {
      clearTimeout(timeoutId);
      unsubscribe?.();
      fn();
    };

    unsubscribe = onSnapshot(
      doc(db, "download_jobs", jobId),
      (snapshot) => {
        if (!snapshot.exists()) return;

        const raw = snapshot.data() as Partial<IDownloadJobDocument>;
        const status = String(raw.status ?? "");
        const progress = typeof raw.progress === "number" ? raw.progress : 0;
        const url = raw.url;

        onProgress?.({
          status,
          progress,
          message: mapDownloadJobStatusToMessage(status),
          url,
        });

        if (status === "completed") {
          if (!url) {
            finish(() => reject(new Error("Descarga completada pero sin URL")));
            return;
          }
          finish(() => resolve(url));
          return;
        }

        if (status === "failed") {
          finish(() =>
            reject(new Error("La descarga ha fallado. Puedes reintentar."))
          );
        }
      },
      (error) => {
        console.error("Error en onSnapshot download_jobs:", error);
        finish(() =>
          reject(
            error instanceof Error
              ? error
              : new Error("Error al seguir el estado de la descarga")
          )
        );
      }
    );
  });
};

/**
 * Patrón async: POST + listener Firestore si aplica
 */
export const downloadStudy = async (
  studyUid: string,
  ext: string
): Promise<string> => {
  try {
    const { job_id, status, url } = await createImagingDownloadJob({
      uid: studyUid,
      ext,
    });

    if (status === "completed" && url) {
      return url;
    }

    return await pollImagingDownloadJobWithProgress(job_id);
  } catch (error) {
    console.log("error in downloadStudy:", error);
    throw error;
  }
};

/**
 * Generate Excel file from imaging data
 * @param {GenerateExcelProps} data - The data to generate the Excel file from
 * @returns {Promise<string>} The Excel file as a xlsx file
 */
export const generateExcel = async (data: GenerateExcelProps): Promise<string> => {
  return http.post<string>(`/report/imaging`, {
    institution_id: data.institution_id,
    date_init: data.date_init,
    date_end: data.date_end,
  });
};

/**
 * Download Excel file from imaging data
 * @param {GenerateExcelProps} data - The data to generate the Excel file from
 * @param {string} filename - The filename for the downloaded file
 * @returns {Promise<void>}
 */
export const downloadImagingExcel = async (
  data: GenerateExcelProps,
  filename: string = "reporte_imaging.xlsx"
): Promise<void> => {
  try {
    // Usar fetch directo sin headers adicionales para evitar problemas de CORS
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/report/imaging?institution_id=${data.institution_id}&date_init=${data.date_init}&date_end=${data.date_end}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading Excel file:", error);
    throw error;
  }
};

/**
 * Get imaging file as blob
 * @param {DownloadImagingProps} data - The data to get the imaging file
 * @returns {Promise<Blob>} The imaging file as blob
 */
export const getImagingFile = async (data: DownloadImagingProps): Promise<Blob> => {
  return http.getBlob(`/imaging/${data.imaging_id}/file`);
};

/**
 * Obtiene la URL firmada para descargar el estudio (ISO / ZIP)
 * @param {string} iuid - El identificador IUID del estudio
 * @param {"iso" | "zip"} format - Formato de descarga
 * @returns {Promise<string>} Promesa con la URL firmada de descarga
 */
export const getDownloadStudyUrl = async (
  iuid: string,
  format: "iso" | "zip"
): Promise<string> => {
  const { url } = await http.get<{ url: string }>(
    `/imaging/donwload/4gb/${iuid}/${format}`
  );
  return url;
};

/**
 * Download imaging file
 * @param {DownloadImagingProps} data - The data to download the imaging file
 * @returns {Promise<void>}
 */
export const downloadImagingFile = async (data: DownloadImagingProps): Promise<void> => {
  const filename = data.filename || `imaging_${data.imaging_id}.dcm`;

  try {
    // Usar fetch directo sin headers adicionales para evitar problemas de CORS
    const response = await fetch(`/imaging/${data.imaging_id}/download`, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading imaging file:", error);
    throw error;
  }
};

/**
 * Get imaging thumbnail as blob
 * @param {string} imaging_id - The imaging ID
 * @returns {Promise<Blob>} The imaging thumbnail as blob
 */
export const getImagingThumbnail = async (imaging_id: string): Promise<Blob> => {
  return http.getBlob(`/imaging/${imaging_id}/thumbnail`);
};

/**
 * Genera un token temporal para acceder a un estudio de imagen
 * @param {ImagingStudyNotification} data - Los datos del estudio de imagen
 * @returns {Promise<ImagingNotificationResponse>} El token temporal
 */
export const generateTemporaryToken = async (
  data: ImagingStudyNotification
): Promise<ImagingNotificationResponse> => {
  return http.post<ImagingNotificationResponse>(
    `${import.meta.env.VITE_API_URL}/imagings/temporary/access`,
    data
  );
};

/**
 * Guarda un registro de estudio compartido en la base de datos
 * @param {SaveSharedRecordProps} data - Los datos del registro compartido
 * @returns {Promise<{ message: string }>} El mensaje de éxito
 */
export const saveSharedRecord = async (
  data: SaveSharedRecordProps
): Promise<{ message: string }> => {
  return http.post<{ message: string }>(`${import.meta.env.VITE_API_URL}/shared`, data);
};

/**
 * Envia una notificación de estudio de imagen mediante el API Gateway
 * @param {NotificationApiPayload} data - Los datos de la notificación
 * @returns {Promise<any>} Response de la API
 */
export const sendStudyNotificationAPI = async (
  data: NotificationApiPayload
): Promise<any> => {
  return http.post<any>(`${import.meta.env.VITE_API_URL}/api/v1/notification`, data);
};

/**
 * Obtiene un token para ver el estudio de imagen
 * @param {string} study_id - El ID del estudio de imagen
 * @returns {Promise<string>} El token para ver el estudio de imagen
 */
export const getTokenViewer = async (study_id: string): Promise<string> => {
  try {
    const token = localStorage.getItem("authToken");
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/v1/viewer/token/${study_id}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as { documents: { data: string } };
    return data.documents.data;
  } catch (error) {
    console.error("Error getting token viewer:", error);
    throw error;
  }
};

/**
 * Elimina un estudio DICOM (cascade: Healthcare API → imagings → shared_studies).
 * El servidor resuelve institution_id a partir del iuid.
 * Endpoint: DELETE /api/v1/imaging/studies/{studyUid}
 */
export const deleteImaging = async (studyUid: string): Promise<void> => {
  return http.delete<void>(`/api/v1/imaging/studies/${encodeURIComponent(studyUid)}`);
};

/** Payload canónico de POST /api/v1/imaging/shared (mismo contrato que clinic). */
export interface SaveSharedStudyApiProps {
  imaging: string;
  user_receiving: string;
  user_sharing: string;
  phone: string[];
}

/**
 * Persiste un registro de estudio compartido vía imaging-api.
 * El servidor escribe shared_studies y sincroniza imagings.shared[].
 */
export const saveSharedStudyApi = async (
  data: SaveSharedStudyApiProps
): Promise<unknown> => {
  return http.post(`/api/v1/imaging/shared`, data);
};
